import { BoxProps, chakra, Flex } from '@chakra-ui/react';
import {
  CreatableSelect,
  Select,
  Size,
  OptionsOrGroups,
  GroupBase,
  Props as ChakraReactSelectProps
} from 'chakra-react-select';

import { IOption } from 'types';

import { CustomFormControl, DirectionProp } from '../CustomFormControl';
import { UndoEdit } from '../../iconButtons/UndoEdit';
import { withConfirm } from '../withConfirm';

interface ICombobox<T> extends DirectionProp {
  options: OptionsOrGroups<T, GroupBase<T>>;
  value?: string[];
  placeholder?: string;
  onChange?: (newValue) => void;
  onCreateOption?: (option: string) => boolean;
  size?: Size;
  selectProps?: ChakraReactSelectProps;
  mt?: BoxProps['mt'];
  mb?: BoxProps['mb'];
  label?: string;
  id?: string;
  name?: string;
  subText?: string;
  errorText?: string;
  helperText?: string;
  canUndo?: boolean;
  onUndo?: () => void;
  isCreatable?: boolean;
  isMulti?: boolean;
  isClearable?: boolean;
  isDisabled?: boolean;
}

const StyledSelect = chakra(Select, {
  baseStyle: {
    margin: 0,
    padding: 0,
    width: '100%'
  }
});
const StyledCreatableSelect = chakra(CreatableSelect, {
  baseStyle: {
    margin: 0,
    padding: 0,
    width: '100%'
  }
});

function getDefaultValue(props: ICombobox<IOption>) {
  const isGroups = props.options.some(o => 'options' in o);
  if (isGroups) {
    let mappedOptions = [];
    for (const group of props.options) {
      const mappedGroupOptions = props?.value
        ? (group as GroupBase<IOption>).options.filter(o => props.value?.includes((o as IOption).value))
        : [];
      mappedOptions = [...mappedOptions, ...mappedGroupOptions];
    }
    return mappedOptions;
  }

  return props?.value ? props.options.filter(o => props.value?.includes((o as IOption).value)) : [];
}

/**
 * Combination selector that implements chakra-react-select with configurable callback functions, state management
 */
export function Combobox(props: ICombobox<IOption>) {
  const defaultOptions = getDefaultValue(props);
  const selectInputId = `${props.id}-input-field`;

  const componentProps = {
    ...props,
    closeMenuOnSelect: !props.isMulti,
    value: defaultOptions,
    inputId: selectInputId,
    openMenuOnFocus: false // for consistency with native Select
  };

  return (
    <CustomFormControl
      id={props.id}
      controlProps={{
        isInvalid: !!props.errorText,
        mb: props.mb ?? 0,
        mt: props.mt ?? 0
      }}
      direction={props.direction ?? 'column'}
      size={props.size}
      label={props.label}
      labelProps={{
        htmlFor: selectInputId
      }}
      subLabel={props.subText}
      helperText={props.helperText}
      error={props.errorText}
    >
      <Flex gap={{ base: 1, lg: 2 }}>
        {props.isCreatable ? (
          <StyledCreatableSelect {...{ ...componentProps, ...props.selectProps }} />
        ) : (
          <StyledSelect {...{ ...componentProps, ...props.selectProps }} />
        )}

        {props.canUndo ? <UndoEdit id={props.id} onUndo={props.onUndo} size={props.size} /> : null}
      </Flex>
    </CustomFormControl>
  );
}

export const ComboboxWithConfirm = withConfirm(Combobox);
